import ArrowBack from "@mui/icons-material/ArrowBack";
import { Box, Breadcrumbs, Button, Link as MUILink, Typography } from "@mui/material";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link, UIMatch, useMatches } from "react-router";

type GaiaRouteHandle = {
    breadcrumb?: string | ((match: UIMatch<unknown, GaiaRouteHandle>) => string | undefined);
    title?: string;
};

type MatchWithTitle = UIMatch<unknown, GaiaRouteHandle> & { title?: string };

export function GaiaBreadcrumbs() {
    const { t } = useTranslation("app");
    const matches = useMatches() as UIMatch<unknown, GaiaRouteHandle>[];
    const matchesWithBreadcrumbs = getMatchesWithBreadcrumbs(t, matches);
    const previousMatch = matchesWithBreadcrumbs[matchesWithBreadcrumbs.length - 2];

    return (
        <>
            <GaiaBreadcrumbsMobile previousMatch={previousMatch} />
            <GaiaBreadcrumbsDesktop matches={matchesWithBreadcrumbs} />
        </>
    );
}

function GaiaBreadcrumbsMobile({ previousMatch }: { previousMatch?: MatchWithTitle }) {
    if (!previousMatch) return null;
    return (
        <Box sx={{ display: { xs: "block", sm: "none" }, minWidth: 0, color: "text.primary" }}>
            <Button
                variant="text"
                color="inherit"
                component={Link}
                to={previousMatch.pathname}
                sx={{
                    width: "100%",
                    gap: 0.5,
                }}
            >
                <ArrowBack fontSize="small" />
                <Typography
                    color="textPrimary"
                    sx={{
                        textTransform: "none",
                        textWrap: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {previousMatch.title}
                </Typography>
            </Button>
        </Box>
    );
}

function GaiaBreadcrumbsDesktop({ matches }: { matches: MatchWithTitle[] }) {
    return (
        <Breadcrumbs
            separator={
                <Typography variant="body2" color="text.secondary">
                    /
                </Typography>
            }
            sx={{
                display: { xs: "none", sm: "block" },
                minWidth: 0,
                "& .MuiBreadcrumbs-ol": {
                    flexWrap: "nowrap",
                },
                "& .MuiBreadcrumbs-li": {
                    overflow: "hidden",
                },
            }}
        >
            {matches.map((match, index) =>
                index < matches.length - 1 ? (
                    <MUILink
                        component={Link}
                        key={match.id}
                        underline="hover"
                        color="inherit"
                        to={match.pathname}
                        variant="body2"
                        sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                        }}
                    >
                        {match.title}
                    </MUILink>
                ) : (
                    <Typography
                        color="textPrimary"
                        key={match.id}
                        component="span"
                        variant="body2"
                        sx={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "block",
                        }}
                    >
                        {match.title}
                    </Typography>
                ),
            )}
        </Breadcrumbs>
    );
}

function getBreadcrumbTitleForMatch(t: TFunction, match: UIMatch<unknown, GaiaRouteHandle>) {
    if (typeof match.handle.breadcrumb === "string") {
        return t(match.handle.breadcrumb);
    }
    if (typeof match.handle.breadcrumb === "function") {
        return match.handle.breadcrumb(match);
    }
    return undefined;
}

function getMatchesWithBreadcrumbs(t: TFunction, matches: UIMatch<unknown, GaiaRouteHandle>[]) {
    try {
        return matches
            .filter(match => match.handle?.breadcrumb !== undefined)
            .map(match => {
                return {
                    ...match,
                    title: getBreadcrumbTitleForMatch(t, match),
                };
            })
            .filter(match => match.title !== undefined);
    } catch (error) {
        // biome-ignore lint/suspicious/noConsole: breadcrumb failure should not crash the shell — log and continue.
        console.error("Error in getMatchesWithBreadcrumbs:", error);
        return [];
    }
}
